// src/components/QoEExecSummary.tsx
import React from 'react';
import { Bot, AlertCircle, FileText, TrendingUp, Check, X, Calendar, User, Settings, Info } from 'lucide-react';
import { AccountEntry, Project, CategoryTemplate } from '../../types';
import { getAllFinancialReports } from '../../lib/data/financialReportsHelper';
import { getSupabase } from '../../lib/api/supabase';
import { exportToWord, downloadBlob } from '../../lib/reportExport';
import { syncAllFinancialReportsToSupabase } from '../../lib/financial/reportSync';

interface QoEExecSummaryProps {
  accounts: AccountEntry[];
  selectedProject: Project | null;
  activeTemplate: CategoryTemplate | null;
  periodType?: 'monthly' | 'quarterly' | 'annual'; // NEW: Period type for AI context
  onNavigateToSettings?: () => void;
}

interface ValidationResult {
  isValid: boolean;
  uncategorizedAccounts: string[];
  missingData: string[];
  warnings: string[];
}

interface ValidationModalProps {
  validationResult: ValidationResult;
  onClose: () => void;
  onGoToCategories: () => void;
}

// Add QoEReport interface
interface QoEReport {
  id: string;
  project_id: string;
  template_id: string;
  report_title: string;
  report_content: string;
  model_id?: string; // Claude model ID used to generate the report
  input_tokens?: number;
  output_tokens?: number;
  total_tokens?: number;
  estimated_cost?: number;
  generation_metadata: {
    accounts_count: number;
    periods_count: number;
    periods_analyzed: string[];
    template_name: string;
    generation_timestamp: string;
  };
  created_at: string;
  created_by: string;
  category_templates?: {
    name: string;
  };
}


const ValidationModal: React.FC<ValidationModalProps> = ({
  validationResult,
  onClose,
  onGoToCategories
}: ValidationModalProps) => {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl">
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <h3 className="text-lg font-medium text-gray-900">Validation Issues Found</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-500">
            <X className="w-5 h-5" />
          </button>
        </div>
        
        <div className="px-6 py-4">
          {validationResult.uncategorizedAccounts.length > 0 && (
            <div className="mb-4">
              <h4 className="font-medium text-red-800 mb-2">
                Uncategorized Accounts ({validationResult.uncategorizedAccounts.length})
              </h4>
              <div className="bg-red-50 border border-red-200 rounded p-3 max-h-32 overflow-y-auto">
                <ul className="text-sm text-red-700 space-y-1">
                  {validationResult.uncategorizedAccounts.slice(0, 10).map((account: string) => (
                    <li key={account}>• {account}</li>
                  ))}
                  {validationResult.uncategorizedAccounts.length > 10 && (
                    <li className="font-medium">... and {validationResult.uncategorizedAccounts.length - 10} more</li>
                  )}
                </ul>
              </div>
            </div>
          )}
          
          {validationResult.missingData.length > 0 && (
            <div className="mb-4">
              <h4 className="font-medium text-yellow-800 mb-2">Missing Data</h4>
              <div className="bg-yellow-50 border border-yellow-200 rounded p-3">
                <ul className="text-sm text-yellow-700 space-y-1">
                  {validationResult.missingData.map((item: string) => (
                    <li key={item}>• {item}</li>
                  ))}
                </ul>
              </div>
            </div>
          )}
          
          {validationResult.warnings.length > 0 && (
            <div className="mb-4">
              <h4 className="font-medium text-blue-800 mb-2">Warnings</h4>
              <div className="bg-blue-50 border border-blue-200 rounded p-3">
                <ul className="text-sm text-blue-700 space-y-1">
                  {validationResult.warnings.map((warning: string) => (
                    <li key={warning}>• {warning}</li>
                  ))}
                </ul>
              </div>
            </div>
          )}
        </div>
        
        <div className="px-6 py-4 bg-gray-50 flex justify-end space-x-3 rounded-b-lg">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-blue-600 border border-transparent rounded-md text-sm font-medium text-white hover:bg-blue-700"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

// Simple markdown-like text formatter
const formatReportText = (text: string): JSX.Element => {
  // Split text into lines and process each line
  const lines = text.split('\n');
  const elements: JSX.Element[] = [];
  
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    
    if (line.startsWith('# ')) {
      // H1 heading
      elements.push(<h1 key={i} className="text-2xl font-bold text-gray-900 mb-4 mt-6">{line.replace('# ', '')}</h1>);
    } else if (line.startsWith('## ')) {
      // H2 heading
      elements.push(<h2 key={i} className="text-xl font-semibold text-gray-900 mb-3 mt-5">{line.replace('## ', '')}</h2>);
    } else if (line.startsWith('### ')) {
      // H3 heading
      elements.push(<h3 key={i} className="text-lg font-semibold text-gray-900 mb-2 mt-4">{line.replace('### ', '')}</h3>);
    } else if (line.startsWith('#### ')) {
      // H4 heading
      elements.push(<h4 key={i} className="text-base font-semibold text-gray-900 mb-2 mt-3">{line.replace('#### ', '')}</h4>);
    } else if (line.startsWith('- ')) {
      // List item - collect consecutive list items
      const listItems = [line.replace('- ', '')];
      let j = i + 1;
      while (j < lines.length && lines[j].startsWith('- ')) {
        listItems.push(lines[j].replace('- ', ''));
        j++;
      }
      elements.push(
        <ul key={i} className="list-disc list-inside mb-4 space-y-1 text-gray-700">
          {listItems.map((item, idx) => (
            <li key={idx}>{formatInlineText(item)}</li>
          ))}
        </ul>
      );
      i = j - 1; // Skip the processed list items
    } else if (line.trim() === '') {
      // Empty line - add spacing
      elements.push(<div key={i} className="mb-2"></div>);
    } else if (line.trim() !== '') {
      // Regular paragraph
      elements.push(<p key={i} className="mb-3 text-gray-700 leading-relaxed">{formatInlineText(line)}</p>);
    }
  }
  
  return <div className="prose max-w-none">{elements}</div>;
};

// Format inline text (bold, italic, etc.)
const formatInlineText = (text: string): React.ReactNode => {
  // Simple bold formatting **text**
  const boldRegex = /\*\*(.*?)\*\*/g;
  const parts = text.split(boldRegex);
  
  return parts.map((part, index) => {
    if (index % 2 === 1) {
      // This is a bold part
      return <strong key={index} className="font-semibold text-gray-900">{part}</strong>;
    }
    return part;
  });
};

// Validation function
// Update the validateCategorization function
const validateCategorization = (accounts: AccountEntry[], activeTemplate: CategoryTemplate | null): ValidationResult => {
    const uniqueAccounts = [...new Set(accounts.map(a => a.accountNumber))];
    const uncategorizedAccounts = uniqueAccounts.filter(accountNumber => {
      const account = accounts.find(a => a.accountNumber === accountNumber);
      return !account?.category || account.category === 'uncategorized' || account.category === 'N/A';
    });
  
    const missingData: string[] = [];
    const warnings: string[] = [];
  
    // Check for missing project
    if (!activeTemplate) {
      missingData.push('No active template selected - Please select a category template');
    }
  
    // Check for sufficient data periods
    const periods = [...new Set(accounts.map(a => a.month))];
    if (periods.length < 3) {
      warnings.push(`Only ${periods.length} period(s) available - QoE analysis is more meaningful with 12+ months. Please upload additional periods if available.`);
    }
  
    // Improved account type detection using template categories
    if (activeTemplate) {
      const categorizedAccounts = accounts.filter(a => a.category && a.category !== 'uncategorized' && a.category !== 'N/A');
      
      // Get account types from template based on actual categorized accounts
      const accountTypes = new Set();
      categorizedAccounts.forEach(account => {
        const templateCategory = activeTemplate.categories.find(c => c.value === account.category);
        if (templateCategory?.type) {
          accountTypes.add(templateCategory.type);
        }
      });
  
      // Check for key account types using template type definitions
      const assetTypes = ['current-asset', 'non-current-asset', 'contra-current-asset', 'contra-non-current-asset'];
      const liabilityTypes = ['current-liability', 'non-current-liability'];
      const revenueTypes = ['revenue', 'other-income'];
      const expenseTypes = ['cost-of-goods-sold', 'operating-expense', 'other-expense'];
  
      const hasAssets = [...accountTypes].some(type => assetTypes.includes(type as string));
      const hasLiabilities = [...accountTypes].some(type => liabilityTypes.includes(type as string));
      const hasRevenue = [...accountTypes].some(type => revenueTypes.includes(type as string));
      const hasExpenses = [...accountTypes].some(type => expenseTypes.includes(type as string));
  
      if (!hasRevenue) warnings.push('No revenue accounts identified - Please categorize revenue accounts in Chart of Accounts');
      if (!hasExpenses) warnings.push('No expense accounts identified - Please categorize expense accounts in Chart of Accounts'); 
      if (!hasAssets) warnings.push('No asset accounts identified - Please categorize asset accounts in Chart of Accounts');
      if (!hasLiabilities) warnings.push('No liability accounts identified - Please categorize liability accounts in Chart of Accounts');
    }
  
    return {
      isValid: uncategorizedAccounts.length === 0 && missingData.length === 0,
      uncategorizedAccounts: uncategorizedAccounts.map(accountNumber => {
        const account = accounts.find(a => a.accountNumber === accountNumber);
        return `${accountNumber} - ${account?.name || 'Unknown'}`;
      }),
      missingData,
      warnings
    };
  };

export function QoEExecSummary({ 
  accounts, 
  selectedProject, 
  activeTemplate,
  periodType = 'monthly', // NEW: Default to monthly for backward compatibility
  onNavigateToSettings
}: QoEExecSummaryProps) {
  const [isGenerating, setIsGenerating] = React.useState(false);
  const [generatedReport, setGeneratedReport] = React.useState<string | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const [showValidationModal, setShowValidationModal] = React.useState(false);
  const [isExporting, setIsExporting] = React.useState(false);
  const [validationWarnings, setValidationWarnings] = React.useState<string[]>([]);

  // Add new state for QoE report management
  const [savedReports, setSavedReports] = React.useState<QoEReport[]>([]);
  const [selectedReportId, setSelectedReportId] = React.useState<string | null>(null);

  // Load QoE reports when project changes
  React.useEffect(() => {
    if (selectedProject) {
      loadQoEReports();
    }
  }, [selectedProject]);

  // Helper functions for QoE reports
  const loadQoEReports = async () => {
    if (!selectedProject) return;
    
    try {
      const supabase = getSupabase();
      const { data, error } = await supabase
        .from('qoe_reports')
        .select(`
          *,
          category_templates(name)
        `)
        .eq('project_id', selectedProject.id)
        .order('created_at', { ascending: false });
      
      if (!error && data) {
        setSavedReports(data);
        // Auto-load most recent report
        if (data.length > 0 && !generatedReport) {
          setGeneratedReport(data[0].report_content);
          setSelectedReportId(data[0].id);
        }
      }
    } catch (error) {
      console.error('Error loading QoE reports:', error);
    }
  };

  const saveQoEReport = async (
    reportContent: string,
    modelId?: string,
    usage?: { input_tokens: number; output_tokens: number; total_tokens: number },
    estimatedCost?: number,
    title?: string
  ) => {
    if (!selectedProject || !activeTemplate) return;

    try {
      const supabase = getSupabase();
      const { data: { user } } = await supabase.auth.getUser();

      const reportData = {
        project_id: selectedProject.id,
        template_id: activeTemplate.id,
        report_title: title || `QoE Report - ${new Date().toLocaleDateString()}`,
        report_content: reportContent,
        model_id: modelId,
        input_tokens: usage?.input_tokens,
        output_tokens: usage?.output_tokens,
        total_tokens: usage?.total_tokens,
        estimated_cost: estimatedCost,
        generation_metadata: {
          accounts_count: [...new Set(accounts.map(a => a.accountNumber))].length,
          periods_count: [...new Set(accounts.map(a => a.month))].length,
          periods_analyzed: [...new Set(accounts.map(a => a.month))],
          template_name: activeTemplate.name,
          generation_timestamp: new Date().toISOString()
        },
        created_by: user?.id
      };

      const { data, error } = await supabase
        .from('qoe_reports')
        .insert([reportData])
        .select(`
          *,
          category_templates(name)
        `)
        .single();

      if (!error && data) {
        setSavedReports(prev => [data, ...prev]);
        setSelectedReportId(data.id);
        console.log('QoE report saved successfully');

        // Log cost information
        if (estimatedCost) {
          console.log(`💰 Report cost: $${estimatedCost.toFixed(4)} (${usage?.total_tokens?.toLocaleString()} tokens)`);
        }
      }
    } catch (error) {
      console.error('Error saving QoE report:', error);
    }
  };

  // Validation check
  const validationResult = React.useMemo(() => 
    validateCategorization(accounts, activeTemplate), 
    [accounts, activeTemplate]
  );

  // Generate button handler - modified to save report
  const handleGenerateReport = async () => {
    if (!validationResult.isValid) {
      setShowValidationModal(true);
      return;
    }

    if (!selectedProject) {
      setError('No project selected');
      return;
    }

    setIsGenerating(true);
    setError(null);

    try {
      // Ensure all financial reports (including fin_ratios) are synced before AI generation.
      if (activeTemplate && accounts.length > 0) {
        console.log('[QoE] Pre-syncing financial reports before generation...');
        await syncAllFinancialReportsToSupabase({
          projectId: selectedProject.id,
          accounts,
          activeTemplate,
          periodType
        });
      }

      // Gather all required data
      console.log('Fetching financial reports for project:', selectedProject.id);
      const financialReports = await getAllFinancialReports(selectedProject.id);
      
      // Get user auth token for the API call
      const supabase = getSupabase();
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      
      if (sessionError) {
        console.error('Failed to get user session:', sessionError);
      }

      // Prepare the comprehensive AI prompt with period-aware context
      const qoePrompt = `You are a Managing Director in the Transaction Advisory Services practice at a Big Four accounting firm, leading the Quality of Earnings workstream for a high-stakes M&A transaction. You are preparing a Quality of Earnings Executive Summary that will be presented directly to the C-suite and Board of Directors of both the buyer and seller.

${getPeriodContextPrompt(periodType)}

${getDataContextPrompt(accounts, periodType)}

${getPeriodSpecificInstructions(periodType)}

Please follow this exact structure:

# Quality of Earnings Executive Summary: ${selectedProject.name}

## A) Overview

[Provide a high-level overview capturing the most critical findings. Summarize key insights regarding the company's quality of earnings, identify major risks and potential red flags, and assess overall performance on both annual and LTM basis.]

## B) Profitability Insights

[Analyze revenue and gross margin trends, elaborate on operating expenses in detail, discuss EBITDA trending and quality.]

## C) Balance Sheet Insights

### Net Working Capital (NWC) Accounts
[Discuss changes and trends in net working capital, analyze efficiency of working capital management, identify red flags related to liquidity. Include analysis of cash conversion cycle trends including DIO, DSO, and DPO trends.]

### Other Assets
[Discuss key trends in non-working capital asset accounts, highlight significant changes in non-current assets, identify potential overstatements or unusual movements.]

### Other Liabilities  
[Analyze trends in non-working capital liability accounts, discuss changes in non-current liabilities, point out unusual or concerning trends.]

### Equity
[Discuss trends in equity accounts, analyze changes in retained earnings and equity components.]

## D) Financial Ratios

[Provide detailed trending analysis covering:
- **Liquidity**: Current Ratio, Quick Ratio analysis
- **Profitability**: Gross/EBITDA/Net Profit Margins, ROA, ROE analysis  
- **Leverage**: Debt ratios, Interest Coverage analysis]

## E) Other Key Findings

[Include flagged accounts, qualitative observations, potential areas for deeper diligence.]

## F) Conclusion

### Recommended Key Follow-up Items
[List specific, actionable steps for further investigation.]

### Big Risks to Diligence  
[Reiterate most significant risks for due diligence prioritization.]

### Overall Quality of Earnings Assessment
[Provide clear assessment: Poor, Fair, Good, or Great - with justification.]

CRITICAL FORMATTING RULES:
- ALL dollar amounts MUST use exact USD with commas (e.g., "$169,772" not "$170K" or "$170M"). Do NOT use K, M, or B abbreviations anywhere in this report.
- When citing financial figures, include the exact source value and period label: e.g., "Cash declined from $169,772 (Dec-25) to $49,959 (Mar-26), a 70.6% decrease."
- Before stating any percentage change, verify your math: percentage change = (new - old) / |old| × 100. Show the calculation inline if the change is a key finding.
- All data values in the provided tables are in actual USD (not thousands, not millions). Use them exactly as given.

Please ensure the analysis:
- Uses clear, professional language suitable for C-suite executives
- Quantifies observations with specific numbers and percentages where possible
- Focuses on trends, insights, and implications rather than just presenting data
- Identifies questions and red flags that suggest risks or areas of concern
- Maintains an objective, analytical tone similar to reports from top-tier accounting firms`;

      // Call the existing AI API with the specialized QoE prompt
      const response = await fetch('/api/ai-financial-analysis', {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          ...(session?.access_token && { 'Authorization': `Bearer ${session.access_token}` })
        },
        body: JSON.stringify({
          query: qoePrompt,
          financialData: accounts,
          context: {
            source: 'qoe-report', // NEW: Identify as QoE report for 32K token limit
            activeTab: 'qoe-exec-summary',
            templateName: activeTemplate?.name,
            projectName: selectedProject?.name,
            projectId: selectedProject?.id,
            periodType: periodType, // NEW: Include period type for AI analysis
            accountCount: [...new Set(accounts.map(a => a.accountNumber))].length,
            dateRange: (() => {
              if (!accounts.length) return 'No data';
              const dates = accounts.map(item => new Date(item.date)).sort();
              return `${dates[0].toLocaleDateString()} to ${dates[dates.length - 1].toLocaleDateString()}`;
            })(),
            periodsAvailable: [...new Set(accounts.map(a => a.month))].length,
            hasMultiplePeriods: [...new Set(accounts.map(a => a.month))].length > 1,
            uncategorizedAccounts: validationResult.uncategorizedAccounts.length,
            financialReports: Object.keys(financialReports).filter(k => financialReports[k] !== null),
            availablePeriods: [...new Set(accounts.map(a => a.month))].sort() // NEW: Specific periods for AI context
          }
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `HTTP ${response.status}: Failed to generate report`);
      }

      const result = await response.json();
      const reportContent = result.analysis || result.content || 'Report generated successfully, but content is missing.';
      const modelId = result.metadata?.model_id;
      const usage = result.metadata?.usage;
      const estimatedCost = result.metadata?.estimated_cost;

      // Post-generation unit validation: flag suspect M/B tokens for a small-company dataset
      const unitWarnings: string[] = [];
      const millionPattern = /\$[\d,]+\.?\d*\s*[MB]\b/gi;
      const millionMatches = reportContent.match(millionPattern);
      if (millionMatches && millionMatches.length > 0) {
        unitWarnings.push(`Possible unit scaling issue: found ${millionMatches.length} value(s) using M/B abbreviations (${millionMatches.slice(0, 3).join(', ')}). Verify these against source data.`);
      }
      if (unitWarnings.length > 0) {
        console.warn('[QoE Validator]', unitWarnings.join(' | '));
        setValidationWarnings(unitWarnings);
      } else {
        setValidationWarnings([]);
      }

      setGeneratedReport(reportContent);

      // Save the report to the database with token usage and cost
      await saveQoEReport(reportContent, modelId, usage, estimatedCost);
      
    } catch (err) {
      console.error('Error generating QoE report:', err);
      setError(err instanceof Error ? err.message : 'Unknown error occurred while generating the report');
    } finally {
      setIsGenerating(false);
    }
  };

  const handleDownloadReport = async () => {
    if (!generatedReport || !selectedProject) return;

    setIsExporting(true);

    try {
      const baseFilename = `QoE_Executive_Summary_${selectedProject.name}_${new Date().toISOString().split('T')[0]}`;
      const reportTitle = `Quality of Earnings Executive Summary - ${selectedProject.name}`;

      const wordBlob = await exportToWord({
        reportContent: generatedReport,
        projectName: selectedProject.name,
        reportTitle,
      });
      downloadBlob(wordBlob, `${baseFilename}.docx`);
    } catch (error) {
      console.error('Error exporting report:', error);
      setError('Failed to export report. Please try again.');
    } finally {
      setIsExporting(false);
    }
  };

  // Add function to handle downloading specific reports
  const handleDownloadSpecificReport = async (report: QoEReport) => {
    if (!selectedProject) return;

    setIsExporting(true);

    try {
      const baseFilename = `${report.report_title}_${new Date(report.created_at).toISOString().split('T')[0]}`;

      const wordBlob = await exportToWord({
        reportContent: report.report_content,
        projectName: selectedProject.name,
        reportTitle: report.report_title,
      });
      downloadBlob(wordBlob, `${baseFilename}.docx`);
    } catch (error) {
      console.error('Error exporting report:', error);
      setError('Failed to export report. Please try again.');
    } finally {
      setIsExporting(false);
    }
  };

  // Helper function to check if project settings are complete
  const isProjectContextComplete = React.useMemo(() => {
    if (!selectedProject) return false;
    return !!(selectedProject.industry?.trim() && selectedProject.business_description?.trim());
  }, [selectedProject]);

  // Helper function to get project context preview
  const getProjectContextPreview = React.useMemo(() => {
    if (!selectedProject) return null;
    const industry = selectedProject.industry?.trim();
    const description = selectedProject.business_description?.trim();
    
    if (!industry && !description) return null;
    
    // For business description, split into words and take first 2 lines worth (roughly 15-20 words each)
    const getDescriptionPreview = (desc: string) => {
      if (!desc) return 'Not specified';
      const words = desc.split(' ');
      if (words.length <= 40) return desc; // If short enough, show all
      
      // Take first 40 words and add ellipsis
      return words.slice(0, 40).join(' ') + '...';
    };
    
    return {
      industry: industry || 'Not specified',
      description: getDescriptionPreview(description || '')
    };
  }, [selectedProject]);

  return (
    <div className="max-w-6xl mx-auto">
      {/* Project Context Indicator */}
      {selectedProject && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Info className="h-5 w-5 text-blue-600" />
              <div>
                <div className="text-sm text-blue-900">
                  <span className="font-medium">Project Context</span> | This context enhances AI-generated QoE reports with industry-specific insights ({getPeriodDisplayText(periodType)})
                </div>
                <div className="text-xs text-blue-700 mt-1 space-y-1">
                  <div><span className="font-medium">Industry:</span> {getProjectContextPreview?.industry || 'Not specified'}</div>
                  <div><span className="font-medium">Business Description:</span> {getProjectContextPreview?.description || 'Not specified'}</div>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {isProjectContextComplete && (
                <div className="flex items-center gap-1 text-green-700">
                  <Check className="h-4 w-4" />
                  <span className="text-sm font-medium">Complete</span>
                </div>
              )}
              <button
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  if (onNavigateToSettings) {
                    onNavigateToSettings();
                  }
                }}
                className="flex items-center gap-1 px-3 py-1 text-sm bg-blue-600 hover:bg-blue-700 text-white rounded-md transition-colors"
              >
                <Settings className="h-4 w-4" />
                Edit Context
              </button>
            </div>
          </div>
        </div>
      )}


      {/* Header with Generate Button */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
        <div className="flex justify-between items-start">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              Quality of Earnings Executive Summary
            </h1>
            {selectedProject && (
              <p className="text-sm text-gray-500 mt-2">
                Project: <span className="font-medium">{selectedProject.name}</span>
              </p>
            )}
          </div>
          
          <div className="flex gap-3">
            {generatedReport && (
              <button
                onClick={handleDownloadReport}
                disabled={isExporting}
                className="bg-green-600 hover:bg-green-700 disabled:bg-gray-400 text-white px-4 py-3 rounded-lg font-medium flex items-center gap-2"
              >
                {isExporting ? (
                  <>
                    <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />
                    Downloading...
                  </>
                ) : (
                  <>
                    <FileText className="h-4 w-4" />
                    Download Report
                  </>
                )}
              </button>
            )}
            <button
              onClick={handleGenerateReport}
              disabled={isGenerating || !selectedProject}
              className="bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white px-6 py-3 rounded-lg font-medium flex items-center gap-2"
            >
              {isGenerating ? (
                <>
                  <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />
                  Generating...
                </>
              ) : (
                <>
                  <Bot className="h-4 w-4" />
                  Generate QoE Report
                </>
              )}
            </button>
          </div>
        </div>

        {/* Validation Status */}
        {!validationResult.isValid && (
          <div className="mt-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
            <div className="flex items-center gap-2">
              <AlertCircle className="h-5 w-5 text-yellow-600" />
              <span className="font-medium text-yellow-800">
                Action Required Before Generation
              </span>
            </div>
            <p className="text-yellow-700 mt-1">
              {validationResult.uncategorizedAccounts.length > 0 && 
                `${validationResult.uncategorizedAccounts.length} accounts need categorization. Please fix the 'Chart of Accounts'. `
              }
              {validationResult.missingData.length > 0 && 
                `Missing: ${validationResult.missingData.join(', ')}. `
              }
              All issues must be resolved before generating the QoE report.
            </p>
          </div>
        )}

        {/* Warnings - Only show if there are actual warnings */}
        {validationResult.warnings.length > 0 && (
        <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="flex items-center gap-2">
            <TrendingUp className="h-5 w-5 text-blue-600" />
            <span className="font-medium text-blue-800">
                Analysis Considerations
            </span>
            </div>
            <ul className="text-blue-700 mt-1 text-sm">
            {validationResult.warnings.map(warning => (
                <li key={warning}>• {warning}</li>
            ))}
            </ul>
        </div>
        )}

        {/* Data Summary - Updated to include Saved Reports */}
        {selectedProject && accounts.length > 0 && (
          <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-gray-50 rounded p-3">
              <div className="text-sm text-gray-600">Total Accounts</div>
              <div className="text-lg font-semibold">{[...new Set(accounts.map(a => a.accountNumber))].length}</div>
            </div>
            <div className="bg-gray-50 rounded p-3">
              <div className="text-sm text-gray-600">Periods</div>
              <div className="text-lg font-semibold">{[...new Set(accounts.map(a => a.month))].length}</div>
            </div>
            <div className="bg-gray-50 rounded p-3">
              <div className="text-sm text-gray-600">Categorized</div>
              <div className="text-lg font-semibold">
                {[...new Set(accounts.map(a => a.accountNumber))].length - validationResult.uncategorizedAccounts.length}
                /{[...new Set(accounts.map(a => a.accountNumber))].length}
              </div>
            </div>
            <div className="bg-gray-50 rounded p-3">
              <div className="text-sm text-gray-600">Saved Reports</div>
              <div className="text-lg font-semibold">{savedReports.length}</div>
            </div>
          </div>
        )}

        {/* Report History Section */}
        {savedReports.length > 0 && (
          <div className="mt-4 bg-gray-50 rounded-lg p-4">
            <h3 className="text-sm font-medium text-gray-700 mb-3">
              Report History ({savedReports.length} reports)
            </h3>
            <div className="space-y-2">
              {savedReports.slice(0, 5).map(report => (
                <div key={report.id} className={`flex items-center justify-between py-2 px-3 bg-white rounded border ${selectedReportId === report.id ? 'ring-2 ring-blue-500' : ''}`}>
                  <div>
                    <div className="font-medium text-sm">{report.report_title}</div>
                    <div className="text-xs text-gray-500">
                      {new Date(report.created_at).toLocaleString()} •
                      {report.category_templates?.name} •
                      {report.generation_metadata?.accounts_count} accounts
                    </div>
                  </div>
                  <div className="flex gap-2 items-center">
                    <button
                      onClick={() => {
                        setGeneratedReport(report.report_content);
                        setSelectedReportId(report.id);
                      }}
                      className={`text-sm px-2 py-1 rounded ${selectedReportId === report.id ? 'bg-blue-100 text-blue-800' : 'text-blue-600 hover:text-blue-800'}`}
                    >
                      {selectedReportId === report.id ? 'Current' : 'Load'}
                    </button>
                    <button
                      onClick={() => handleDownloadSpecificReport(report)}
                      disabled={isExporting}
                      className="text-green-600 hover:text-green-800 text-sm px-2 py-1 rounded hover:bg-green-50 flex items-center gap-1"
                    >
                      <FileText className="h-3 w-3" />
                      Download
                    </button>
                  </div>
                </div>
              ))}
              {savedReports.length > 5 && (
                <div className="text-center text-sm text-gray-500 pt-2">
                  ... and {savedReports.length - 5} more reports
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Unit Validation Warning Banner */}
      {validationWarnings.length > 0 && (
        <div className="bg-amber-50 border border-amber-300 rounded-lg p-4 mb-4">
          <div className="flex items-start gap-2">
            <AlertCircle className="h-5 w-5 text-amber-600 mt-0.5 flex-shrink-0" />
            <div>
              <span className="font-medium text-amber-800">Unit Validation Warning</span>
              {validationWarnings.map((w, i) => (
                <p key={i} className="text-sm text-amber-700 mt-1">{w}</p>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Generated Report Display - Updated to show report metadata */}
      {generatedReport && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8">
          {selectedReportId && (
            <div className="flex items-center justify-between mb-4 pb-4 border-b">
              <h2 className="text-lg font-semibold text-gray-900">Generated Report</h2>
              <div className="flex items-center gap-2 text-sm text-gray-500">
                <Calendar className="h-4 w-4" />
                {savedReports.find(r => r.id === selectedReportId)?.created_at && 
                  new Date(savedReports.find(r => r.id === selectedReportId)!.created_at).toLocaleString()
                }
              </div>
            </div>
          )}
          {formatReportText(generatedReport)}
        </div>
      )}

      {/* Error Display */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center gap-2">
            <AlertCircle className="h-5 w-5 text-red-600" />
            <span className="font-medium text-red-800">Error</span>
          </div>
          <p className="text-red-700 mt-1">{error}</p>
        </div>
      )}

      {/* Validation Modal */}
      {showValidationModal && (
        <ValidationModal 
          validationResult={validationResult}
          onClose={() => setShowValidationModal(false)}
         onGoToCategories={() => {
           setShowValidationModal(false);
           // Navigate to categories tab - this would need to be passed as a prop or handled by parent
         }}
       />
     )}

   </div>
 );
}

// Period-aware helper functions
function getPeriodDisplayText(periodType: 'monthly' | 'quarterly' | 'annual'): string {
  const displayTexts = {
    monthly: 'Monthly View',
    quarterly: 'Quarterly View', 
    annual: 'Annual View'
  };
  return displayTexts[periodType];
}

function getPeriodContextPrompt(periodType: 'monthly' | 'quarterly' | 'annual'): string {
  const contextMap = {
    monthly: `PERIOD CONTEXT: You are analyzing MONTHLY financial data. Focus on:
- Most recent month performance vs. previous months
- Monthly trending and seasonality patterns
- Last 12 months (LTM) performance for annualized analysis
- Month-over-month growth rates and variations
- Seasonal impacts and cyclical patterns within the year`,

    quarterly: `PERIOD CONTEXT: You are analyzing QUARTERLY financial data. Focus on:
- Most recent quarter performance vs. previous quarters  
- Quarterly trending and year-over-year comparisons
- Last 12 months (LTM) performance for full-year view
- Quarter-over-quarter growth rates
- Seasonal quarterly patterns and business cycles`,

    annual: `PERIOD CONTEXT: You are analyzing ANNUAL financial data. Focus on:
- Year-over-year performance trends
- Annual growth rates and multi-year patterns
- Long-term financial trajectory (3-5 years if available)
- Annual performance consistency
- Multi-year strategic initiatives impact`
  };
  
  return contextMap[periodType];
}

function getDataContextPrompt(accounts: AccountEntry[], periodType: 'monthly' | 'quarterly' | 'annual'): string {
  const availablePeriods = [...new Set(accounts.map(a => a.month))].sort();
  const periodCount = availablePeriods.length;
  
  const baseContext = `DATA CONTEXT: This analysis contains ${periodCount} ${periodType} periods: ${availablePeriods.join(', ')}.`;
  
  const instructionMap = {
    monthly: `${baseContext}
IMPORTANT: The financial tables include monthly, YTD, and LTM columns:
- Use LTM columns (e.g., 'LTM May-25') for most recent 12-month performance
- Use monthly columns (e.g., 'Jan-24', 'Feb-24') for month-to-month trends and seasonality
- Use YTD columns (e.g., 'YTD 2025') for current year-to-date vs. prior YTD comparisons
- Always reference the exact column labels when citing figures`,

    quarterly: `${baseContext}
IMPORTANT: The financial tables include quarterly, YTD, and LTM columns:
- Use LTM columns (e.g., 'LTM Q1-25') for most recent 12-month performance  
- Use quarterly columns (e.g., 'Q1-24', 'Q2-24') for quarter-to-quarter trends
- Use YTD columns (e.g., 'YTD 2025') for current year-to-date vs. prior YTD comparisons
- Always reference the exact column labels when citing figures`,

    annual: `${baseContext}
IMPORTANT: The financial tables include annual and multi-year columns:
- Use most recent annual columns (e.g., '2024', '2025') for current performance
- Use historical annual columns for year-over-year trend analysis
- Focus on annual growth rates and multi-year patterns
- Always reference the exact column labels when citing figures`
  };
  
  return instructionMap[periodType];
}

function getPeriodSpecificInstructions(periodType: 'monthly' | 'quarterly' | 'annual'): string {
  const instructionMap = {
    monthly: `MONTHLY ANALYSIS FOCUS:
- Highlight monthly volatility and seasonal patterns
- Identify any months with unusual performance (both positive and negative)
- Assess business seasonality and monthly cash flow patterns
- Comment on recent monthly trends (last 3-6 months)
- Evaluate monthly working capital fluctuations`,

    quarterly: `QUARTERLY ANALYSIS FOCUS:
- Emphasize quarterly business cycles and seasonal strength/weakness
- Compare quarterly performance year-over-year (Q1 2025 vs Q1 2024)
- Identify quarterly trends and momentum
- Assess quarterly earnings quality and consistency
- Evaluate quarterly working capital management`,

    annual: `ANNUAL ANALYSIS FOCUS:
- Emphasize multi-year trends and long-term trajectory
- Focus on annual growth rates and compound growth
- Assess consistency of annual performance
- Identify strategic initiatives impact over multiple years
- Evaluate long-term financial health and sustainability`
  };
  
  return instructionMap[periodType];
}